import {useQuery} from '@tanstack/react-query';
import {useFilterEvent} from './useFilterEvent';
import {useAppSelector} from '../hooks';
import {apiRequest} from '../../utils/AxiosHelper';
import API from '../../modules/settings/APIs';
import {EventFilterCriteria, LossEvent} from '../../types/events';
import {PaginationState} from '../../types';

export interface EventFilterState {
  searchText: string;
  status?: string;
  severityTier?: string;
  lossEventType?: string;
  businessLine?: string;
  dateRange: [string, string] | null;
  hasFilters: boolean;
}

export const defaultEventFilters: EventFilterState = {
  searchText: '',
  status: undefined,
  severityTier: undefined,
  lossEventType: undefined,
  businessLine: undefined,
  dateRange: null,
  hasFilters: false,
};

export const useEventsData = (
  filters: EventFilterState,
  pagination: PaginationState,
  enabled = true
) => {
  const {isAuthenticated, token} = useAppSelector(s => s.session);

  const {data: filterOptions, isLoading: isLoadingOptions} = useQuery({
    queryKey: ['events', 'filterOptions'],
    queryFn: async () => {
      const response = await apiRequest.get(API.events.filter.options);
      return response.data.data || response.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });

  const searchCriteria: EventFilterCriteria = {
    searchText: filters.searchText || undefined,
    status:         filters.status || undefined,
    severityTier:   filters.severityTier || undefined,
    lossEventType:  filters.lossEventType || undefined,
    businessLine:   filters.businessLine || undefined,
    occurrenceDateFrom: filters.dateRange?.[0] || undefined,
    occurrenceDateTo:   filters.dateRange?.[1] || undefined,
    offset: (pagination.current - 1) * pagination.pageSize,
    limit: pagination.pageSize,
  };

  const {
    data: searchResponse,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
  } = useFilterEvent(searchCriteria, {enabled: enabled && isAuthenticated && !!token});

  const totalCount = searchResponse?.totalCount || 0;
  const searchResults: LossEvent[] = searchResponse?.data || [];

  return {
    searchResults,
    totalCount,
    filterOptions,
    isSearchLoading,
    isLoadingOptions,
    isSearchError,
    searchError,
  };
};
